import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DOCUMENT_CATEGORIES } from '../types';
import {
  EMPLOYEE_DOC_REQUIREMENTS,
  EmployeeDocRequirement,
} from '../components/library-list/types';

/**
 * The Library page keeps its view state in the URL query, e.g.
 * `/library?category=business&workflow=Registration&region=uk` or
 * `/library?libraryType=own&category=customers`.
 * Every view the user sees has its own history entry, so the browser Back button from a library
 * item details page returns to exactly the same library (Business Kitz / Own), category,
 * workflow, card/list view, region, search and requirement filter.
 * The requirement filter is a repeated param: `?requirement=all&requirement=casual`.
 */
export const LIBRARY_QUERY_PARAMS = {
  LIBRARY_TYPE: 'libraryType',
  CATEGORY: 'category',
  WORKFLOW: 'workflow',
  VIEW: 'view',
  REGION: 'region',
  SEARCH: 'search',
  /** Employment requirement filter (repeated param, one value per selected requirement). */
  REQUIREMENT: 'requirement',
  /** Legacy flags, still used by links from other pages (e.g. the app header). */
  LEGACY_BUSINESS_KITZ: 'businessKitzLibrary',
  LEGACY_OWN: 'ownLibrary',
} as const;

export type LibraryType = 'businessKitz' | 'own';
export type LibraryRegion = 'uk' | 'au' | 'global';

const LIBRARY_LIST_VIEW = 'list';
const OWN_LIBRARY = 'own';
const DEFAULT_REGION: LibraryRegion = 'au';
const LIBRARY_REGIONS: LibraryRegion[] = ['au', 'uk', 'global'];
const LIBRARY_CATEGORIES: DOCUMENT_CATEGORIES[] = [
  'Business',
  'Employment',
  'Customers',
  'Suppliers',
];

export type LibraryQueryState = {
  libraryType: LibraryType;
  category: DOCUMENT_CATEGORIES;
  /** Selected workflow card (Business Kitz library, card view, a category chosen). */
  workflow: string | null;
  cardView: boolean;
  region: LibraryRegion;
  /** Committed "search by name" value. */
  search: string;
  /**
   * Selected `employee_doc_requirements` filter values (Business Kitz library, Employment
   * category, list view only), in the canonical EMPLOYEE_DOC_REQUIREMENTS order.
   * Empty = no filter.
   */
  employeeDocRequirements: EmployeeDocRequirement[];
};

/**
 * The requirement filter is available only in the list view of the Business Kitz library,
 * Employment category (the card view, including its workflow lists, is never filtered).
 */
export const isRequirementFilterAvailable = (
  libraryType: LibraryType,
  category: DOCUMENT_CATEGORIES,
  cardView: boolean
): boolean => libraryType === 'businessKitz' && category === 'Employment' && !cardView;

/** Keeps known values only, without duplicates, in the canonical order. */
export const normalizeEmployeeDocRequirements = (
  values: readonly string[]
): EmployeeDocRequirement[] => {
  const selected = new Set(
    values.flatMap((value) => value.split(',')).map((value) => value.trim().toLowerCase())
  );
  return EMPLOYEE_DOC_REQUIREMENTS.filter((requirement) => selected.has(requirement));
};

const parseLibraryType = (params: URLSearchParams): LibraryType => {
  const value = params.get(LIBRARY_QUERY_PARAMS.LIBRARY_TYPE);
  if (value === OWN_LIBRARY) {
    return 'own';
  }
  if (value === 'businessKitz' || params.has(LIBRARY_QUERY_PARAMS.LEGACY_BUSINESS_KITZ)) {
    return 'businessKitz';
  }
  return params.has(LIBRARY_QUERY_PARAMS.LEGACY_OWN) ? 'own' : 'businessKitz';
};

const parseCategory = (value: string | null): DOCUMENT_CATEGORIES => {
  if (!value) {
    return 'All';
  }
  const normalized = value.trim().toLowerCase();
  return LIBRARY_CATEGORIES.find((category) => category.toLowerCase() === normalized) ?? 'All';
};

const parseRegion = (value: string | null): LibraryRegion =>
  LIBRARY_REGIONS.find((region) => region === value?.toLowerCase()) ?? DEFAULT_REGION;

export const readLibraryQuery = (params: URLSearchParams): LibraryQueryState => {
  const libraryType = parseLibraryType(params);
  const category = parseCategory(params.get(LIBRARY_QUERY_PARAMS.CATEGORY));
  const cardView = params.get(LIBRARY_QUERY_PARAMS.VIEW) !== LIBRARY_LIST_VIEW;
  return {
    libraryType,
    category,
    workflow: params.get(LIBRARY_QUERY_PARAMS.WORKFLOW) || null,
    cardView,
    region: parseRegion(params.get(LIBRARY_QUERY_PARAMS.REGION)),
    search: params.get(LIBRARY_QUERY_PARAMS.SEARCH) ?? '',
    employeeDocRequirements: isRequirementFilterAvailable(libraryType, category, cardView)
      ? normalizeEmployeeDocRequirements(params.getAll(LIBRARY_QUERY_PARAMS.REQUIREMENT))
      : [],
  };
};

/**
 * Returns a copy of `params` with the library state written in its canonical form.
 * Defaults are omitted (Business Kitz library, "All", no workflow, card view, "au", no search,
 * no requirement filter).
 * A workflow is kept only where it can be shown: Business Kitz library with a category chosen.
 * The requirement filter is kept only for the Business Kitz library, Employment category,
 * list view, so switching to the card view (or another category / library) clears it.
 */
export const writeLibraryQuery = (
  params: URLSearchParams,
  state: LibraryQueryState
): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.delete(LIBRARY_QUERY_PARAMS.LEGACY_BUSINESS_KITZ);
  next.delete(LIBRARY_QUERY_PARAMS.LEGACY_OWN);

  const setOrDelete = (key: string, value: string | null) => {
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
  };

  setOrDelete(LIBRARY_QUERY_PARAMS.LIBRARY_TYPE, state.libraryType === 'own' ? OWN_LIBRARY : null);
  setOrDelete(
    LIBRARY_QUERY_PARAMS.CATEGORY,
    state.category === 'All' ? null : state.category.toLowerCase()
  );
  setOrDelete(
    LIBRARY_QUERY_PARAMS.WORKFLOW,
    state.libraryType === 'businessKitz' && state.category !== 'All' ? state.workflow : null
  );
  setOrDelete(LIBRARY_QUERY_PARAMS.VIEW, state.cardView ? null : LIBRARY_LIST_VIEW);
  setOrDelete(LIBRARY_QUERY_PARAMS.REGION, state.region === DEFAULT_REGION ? null : state.region);
  setOrDelete(LIBRARY_QUERY_PARAMS.SEARCH, state.search || null);

  next.delete(LIBRARY_QUERY_PARAMS.REQUIREMENT);
  if (isRequirementFilterAvailable(state.libraryType, state.category, state.cardView)) {
    normalizeEmployeeDocRequirements(state.employeeDocRequirements).forEach((requirement) =>
      next.append(LIBRARY_QUERY_PARAMS.REQUIREMENT, requirement)
    );
  }

  return next;
};

export type UpdateLibraryQuery = (
  changes: Partial<LibraryQueryState>,
  options?: { replace?: boolean }
) => void;

/**
 * Reads / writes the library view state from / to the URL. Every write starts from the current
 * browser URL, so several writes in a row never overwrite each other with stale values.
 *
 * @param normalize - rewrite legacy / invalid params in place (use it in one place per page).
 */
export const useLibraryQuery = (normalize = false) => {
  const [params, setSearchParams] = useSearchParams();
  const state = useMemo(() => readLibraryQuery(params), [params]);

  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const updateLibraryQuery: UpdateLibraryQuery = useCallback(
    (changes, options) => {
      // Async callbacks and unmount clean-ups must not change the URL of another page.
      if (!isMountedRef.current) {
        return;
      }
      const current = new URLSearchParams(window.location.search);
      const next = writeLibraryQuery(current, { ...readLibraryQuery(current), ...changes });
      if (next.toString() !== current.toString()) {
        setSearchParams(next, { replace: options?.replace ?? false });
      }
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (!normalize) {
      return;
    }
    const canonical = writeLibraryQuery(params, readLibraryQuery(params));
    if (canonical.toString() !== params.toString()) {
      setSearchParams(canonical, { replace: true });
    }
  }, [normalize, params, setSearchParams]);

  return { ...state, updateLibraryQuery };
};
